import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import debounce from 'lodash.debounce'
import { Button, Stack, Typography } from '@mui/material'
import { OverlayState } from '@/hooks/useOverlay'
import { useGetVendorsLiveList } from '@/services/hooks/relay/live-lists'
import { VendorLiveListEntity } from '@/types/live-lists'
import { Project } from '@/types/projects'
import { RollupConfigVendor } from '@/types/rollup'
import generateUuid from '@/utils/uuid'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'
import { FormAutocomplete, PixyDocsForm } from '@/components/forms'
import { useInfiniteScrollListbox } from '@/components/validation/fields/InfiniteScrollListBox'
import getVendorNum from './getVendorNum'

// FormAutocomplete options must carry an `id`; live-list entities key on
// `entity_id` instead, so options are mapped into this shape.
type VendorOption = VendorLiveListEntity & { id: string }

type FormValues = {
  vendor: VendorOption | null
}

const defaultValues: FormValues = { vendor: null }

type AddVendorDialogProps = {
  overlay: OverlayState
  project: Project
  companyId: string
  companyName: string
  /** Vendor numbers already on this card — re-adding one is a no-op the user
   * shouldn't be offered. */
  currentVendorNums: Set<string>
  /** Vendor numbers targeted by a different config for this same company —
   * a vendor_num may only appear in one config per (project, company). */
  claimedVendorNums: Set<string>
  onAdd: (vendor: RollupConfigVendor) => void
}

export default function AddVendorDialog({
  overlay,
  project,
  companyId,
  companyName,
  currentVendorNums,
  claimedVendorNums,
  onAdd,
}: AddVendorDialogProps) {
  const [inputValue, setInputValue] = useState('')
  const [searchValue, setSearchValue] = useState('')

  const methods = useForm<FormValues>({ defaultValues })
  const {
    reset,
    formState: { isValid },
  } = methods

  const hasOrgCredentialType = !!project.org_credential_type_id

  const {
    allData: vendors,
    hasNextPage,
    fetchNextPage,
    isFetching: isFetchingVendors,
  } = useGetVendorsLiveList({
    orgId: companyId,
    orgCredentialTypeId: project.org_credential_type_id || '',
    filters: {
      query: searchValue,
      fields__only: 'entity_id,pd_display_name,external_vendor_id',
      limit: '20',
    },
    enabled: overlay.isOpen && !!companyId && hasOrgCredentialType,
    placeholderData: (previousData) => previousData,
  })

  const vendorOptions = useMemo<VendorOption[]>(
    () => vendors.map((v) => ({ ...v, id: v.entity_id })),
    [vendors],
  )

  const debouncedSearch = useMemo(
    () => debounce((newValue: string) => setSearchValue(newValue), 200),
    [],
  )

  useEffect(() => {
    return () => {
      debouncedSearch.cancel()
    }
  }, [debouncedSearch])

  const { listboxSlotProps } = useInfiniteScrollListbox({
    hasNextPage,
    fetchNextPage,
    isFetching: isFetchingVendors,
    optionsLength: vendorOptions.length,
  })

  const resetState = () => {
    reset(defaultValues)
    setInputValue('')
    setSearchValue('')
    debouncedSearch.cancel()
  }

  const getDisabledReason = (option: VendorOption) => {
    const vendorNum = getVendorNum(option)
    if (currentVendorNums.has(vendorNum)) return 'Added'
    if (claimedVendorNums.has(vendorNum)) return 'In another config'
    return null
  }

  const handleSubmit = ({ vendor }: FormValues) => {
    if (!vendor) return
    onAdd({
      id: generateUuid(),
      vendor_num: getVendorNum(vendor),
      display_name: vendor.pd_display_name,
    })
    overlay.close()
  }

  return (
    <Dialog
      maxWidth="sm"
      {...overlay}
      onClose={resetState}
      title="Add Vendor"
      subtitle={`Search vendors for ${companyName}.`}
    >
      <PixyDocsForm methods={methods} onSubmit={handleSubmit}>
        <DialogContent>
          <FormAutocomplete<FormValues, VendorOption>
            name="vendor"
            label="Vendor"
            required
            options={vendorOptions}
            loading={isFetchingVendors}
            filterOptions={(options) => options}
            getOptionLabel={(option) => option.pd_display_name || ''}
            isOptionEqualToValue={(option, selected) =>
              option.id === selected.id
            }
            getOptionDisabled={(option) => !!getDisabledReason(option)}
            renderOption={({ key, ...optionProps }, option) => {
              const disabledReason = getDisabledReason(option)
              return (
                <li key={key} {...optionProps}>
                  <Stack>
                    <Typography variant="body2">
                      {option.pd_display_name}
                    </Typography>
                    {disabledReason && (
                      <Typography variant="caption" color="text.secondary">
                        {disabledReason}
                      </Typography>
                    )}
                  </Stack>
                </li>
              )
            }}
            inputValue={inputValue}
            onInputChange={(_e, newInputValue, reason) => {
              // Always take MUI's computed value. Selecting an option fires
              // this with reason 'reset' and the option's label — dropping it
              // would blank the field even though the vendor is selected.
              setInputValue(newInputValue)
              if (reason === 'input') {
                debouncedSearch(newInputValue)
                return
              }
              // 'reset' and 'clear' jump the input to a known value rather
              // than typing toward one, so a pending keystroke search is
              // stale. Only re-query when the field actually ends up empty —
              // re-querying on selection would refetch the whole list right
              // as the user reaches for Add vendor.
              debouncedSearch.cancel()
              if (!newInputValue) setSearchValue('')
            }}
            textFieldProps={{ placeholder: 'Search vendors', autoFocus: true }}
            slotProps={{ listbox: listboxSlotProps }}
          />
        </DialogContent>

        <DialogFooter>
          <Button variant="text" onClick={overlay.close}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={!isValid}>
            Add vendor
          </Button>
        </DialogFooter>
      </PixyDocsForm>
    </Dialog>
  )
}
